import React, {useCallback, useEffect, useRef, useState} from 'react';
import {observer} from 'mobx-react';
import Box from '../Common/Box';
import shadowClient from '../lib/clients/shadow/shadowClient';
import Logger from '../lib/Logger';
import MessageUI from './Message/Message';
import {TypingIndicator} from "./TypingIndicator";
import {Message} from "../lib/stores/MessageStore";
import MessageInput from "./MessageInput";
import uiStore from "../lib/stores/uiStore";
import {createPropertyTween} from "../lib/three/tweens";
import ControlButton from "../Common/ControlButton";
import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown';
import Lock from '@mui/icons-material/Lock';
import {Fade} from "@mui/material";
import ElvishGate from "../Main/ElvishGate";
import {ClientEvent, RoomSyncData} from "../lib/clients/shadow/interfaces";

const logger = new Logger("Messages");

const Messages = () => {

    const [messages, setMessages] = useState<Message[]>([]);

    const handleMessage = useCallback(function (message: Message) {
        setMessages([...messages, message])
    }, [messages]);

    const handleDelete = useCallback(function (deletedMessage: Message) {
        const newMessages = messages.filter(message => message.id !== deletedMessage.id);
        setMessages(newMessages)
    }, [messages]);

    const handleRoomSynced = useCallback(function ({messages}: RoomSyncData) {
        setMessages([...messages])
    }, [messages]);

    const [scrolledUp, setScrolledUp] = useState(false);
    const [locked, setLocked] = useState(true);
    const rootRef = useRef(null);
    const containerRef = useRef(null);
    const innerRef = useRef(null);

    const tweenRef = useRef(null);
    const lockedRef = useRef(true);
    const debounceRef = useRef(null);
    const pointerDownRef = useRef(false);

    function scrollTo(target: number, options: { duration?: number } = {}) {
        if (!containerRef.current) {
            logger.error("tried to scroll without rootRef present");
            return;
        }
        logger.info("starting scroll tween. scrolling to " + target);
        logger.info("starting scroll tween. scrollHeight: " + containerRef.current.scrollHeight);

        if (tweenRef.current) {
            tweenRef.current.stop();
        }

        const {
            duration = 500
        } = options;

        const tween = createPropertyTween(containerRef.current, "scrollTop", target,
                {
                    duration,
                    onUpdate: () => {
                        logger.info("updating scroll tween. scrollTop: " + containerRef.current.scrollTop);
                    },
                    onComplete: () => {
                        if (containerRef.current) {
                            const {scrollHeight, scrollTop, clientHeight} = containerRef.current;
                            const distanceToBottom = scrollHeight - scrollTop - clientHeight;
                            setScrolledUp(distanceToBottom > 100);
                        }

                        tweenRef.current = null;

                    }
                });
        tweenRef.current = tween;
        tween.start()

    }


    useEffect(() => {
        if (!rootRef.current) {
            return;
        }

        setMessages([...shadowClient.messageStore.messages]);

        uiStore.messagesWidth = rootRef.current.clientWidth;

        const observer = new ResizeObserver(() => {
            if (rootRef.current) {
                uiStore.messagesWidth = rootRef.current.clientWidth;
            }
        });

        const cobserver = new ResizeObserver(() => {
            if (lockedRef.current && containerRef.current) {
                scrollTo(containerRef.current.scrollHeight, {duration: 0})
            }
        });

        const iobserver = new ResizeObserver(() => {
            if (lockedRef.current && containerRef.current) {
                const {scrollHeight, scrollTop, clientHeight} = containerRef.current;
                const distance = scrollHeight - clientHeight - scrollTop;
                const duration = distance > 1000 ? 0 : 500;
                scrollTo(scrollHeight, {duration})
            }
        });

        observer.observe(rootRef.current);

        cobserver.observe(containerRef.current);
        iobserver.observe(innerRef.current);
        //
        // getMessages().catch((e: any) => {
        //     notifier.error("Error while getting messages. Error: " + e.message);
        //     logger.error("Error while getting messages: %s", e);
        // });

        return () => {
            observer.disconnect();
            cobserver.disconnect();
            iobserver.disconnect();
        }

    }, [rootRef.current]);

    useEffect(() => {

        shadowClient.messageStore.on(ClientEvent.MESSAGE_CREATE, handleMessage);
        shadowClient.messageStore.on(ClientEvent.MESSAGE_DELETE, handleDelete);
        shadowClient.on(ClientEvent.ROOM_SYNCED, handleRoomSynced);

        return () => {
            shadowClient.messageStore.removeListener(ClientEvent.MESSAGE_CREATE, handleMessage);
            shadowClient.messageStore.removeListener(ClientEvent.MESSAGE_DELETE, handleDelete);
            shadowClient.removeListener(ClientEvent.ROOM_SYNCED, handleRoomSynced);
        }

    }, [handleMessage, handleRoomSynced]);

    // const Row = React.forwardRef(
    //         ({index, height, style, observer}: { index: number, height: string, style: any, observer: boolean },
    //          ref: React.ForwardedRef<HTMLDivElement>) => {
    //
    //             const message = messages[index];
    //
    //             return <div
    //                     ref={ref}
    //                     style={style}
    //             >
    //                 <MessageUI message={message}
    //                 />
    //             </div>
    //
    //         });


    const slicedMessages = messages.slice(-40);

    function onPointerDown() {
        pointerDownRef.current = true;
        if (tweenRef.current) {
            tweenRef.current.stop();
            tweenRef.current = null;
        }

        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
            debounceRef.current = null;
        }
    }

    function checkScroll(event: React.UIEvent, debounce?: boolean) {
        if (!containerRef.current) {
            return;
        }

        if (event.type === "scroll") {
            const {scrollHeight, scrollTop, clientHeight} = containerRef.current;
            const distanceToBottom = scrollHeight - scrollTop - clientHeight;
            const duration = distanceToBottom > 1000 ? 0 : 500;
            const newScrolledUp = distanceToBottom > 100;
            const newLocked = distanceToBottom < 15;

            if (!pointerDownRef.current) {
                if (lockedRef.current) {
                    if (!tweenRef.current) {
                        logger.trace("starting instant scroll on scroll event");
                        scrollTo(scrollHeight, {duration})
                    }
                }

            } else {
                return;
            }

            if (!debounceRef.current) {
                debounceRef.current = setTimeout(() => checkScroll(event, false), 500);
                return;
            } else if (debounce !== false) {
                clearTimeout(debounceRef.current);
                debounceRef.current = setTimeout(() => checkScroll(event, false), 500);
                return;
            } else {
                debounceRef.current = null;

                if (scrolledUp !== newScrolledUp) {
                    setScrolledUp(newScrolledUp);
                }

                if (newLocked && lockedRef.current !== newLocked) {
                    lockedRef.current = newLocked;
                    setLocked(newLocked);
                }

                if (lockedRef.current) {
                    scrollTo(scrollHeight, {duration})
                }

                return;
            }
        } else {

            pointerDownRef.current = false

        }

        const {scrollHeight, scrollTop, clientHeight} = containerRef.current;
        const distanceToBottom = scrollHeight - scrollTop - clientHeight;
        const newScrolledUp = distanceToBottom > 100;
        const newLocked = distanceToBottom < 15;

        if (scrolledUp !== newScrolledUp) {
            setScrolledUp(newScrolledUp);
        }

        if (locked !== newLocked) {
            lockedRef.current = newLocked;
            setLocked(newLocked);
        }

    }

    const joinedRoom = shadowClient.getJoinedRoom();

    if (!joinedRoom) {
        return <ElvishGate
                // color={theme.palette.background.paper}
        />
    }

    return <Box
            ref={rootRef}
            id={"messages"}
            expand={!uiStore.isMobile}
            sx={{
                flex: 1,
                overflow: "auto",
            }}
            column
    >
        <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    overflow: "hidden",
                    flex: 1,
                    position: "relative"
                }}
        >
            <div
                    onScroll={checkScroll}
                    ref={containerRef}
                    style={{
                        width: "100%",
                        overflow: "auto",
                        flex: 1,
                        position: "relative"
                    }}
                    onTouchStart={onPointerDown}
                    onMouseDown={onPointerDown}
                    onTouchEnd={checkScroll}
                    onMouseUp={checkScroll}
            >
                <div
                        ref={innerRef}
                >
                    {
                        slicedMessages.map((message) => {

                            return <MessageUI key={message.id} message={message}/>
                        })
                    }
                    {uiStore.isMobile && <Box height={"15px"}/>}
                </div>

            </div>
            {uiStore.isMobile &&
                    <div
                            style={{
                                pointerEvents: "none",
                                position: "absolute",
                                /* z-index: 10000; */
                                bottom: "0px",
                                zIndex: "10px",
                                width: "100%",
                            }}
                    >
                        <Fade in={scrolledUp}>
                            <ControlButton
                                    sx={{
                                        position: "absolute",
                                        pointerEvents: "all",
                                        background: "#454545",
                                        right: "10px",
                                        zIndex: "10px",
                                        bottom: "5px",
                                    }}
                                    onClick={() => {
                                        scrollTo(containerRef.current.scrollHeight, {duration: 500})
                                    }}
                                    icon={KeyboardDoubleArrowDownIcon}
                            />
                        </Fade>
                        {uiStore.isMobile && <TypingIndicator size={"6px"}/>}

                    </div>
            }
            {locked && <ControlButton
                    sx={{
                        zIndex: 1000,
                        position: "absolute",
                        bottom: "-3px",
                        right: uiStore.isMobile ? "5px" : "15px",
                        color: "#000222",
                        opacity: 0.1,
                    }}
                    width={25}
                    icon={Lock}
            />}
        </div>
        {/*{uiStore.messagesWidth && uiStore.messagesVisible ? <List*/}
        {/*        withRenderMessage*/}
        {/*        width={"100%"}*/}
        {/*        items={messages}*/}
        {/*        style={{flex: 1}}*/}
        {/*>*/}
        {/*    {Row}*/}
        {/*</List> : <Box overflow={"auto"} id={"empty-list"} flex={1} pointerEvents={"none"}/>}*/}


        <MessageInput/>
        {!uiStore.isMobile && <Box height={"25px"}><TypingIndicator/></Box>}
    </Box>
};

export default observer(Messages);
